import { useState } from 'react';

import NestedFirstRow from './NestedFirstRow';
import NonNestedRow from './NonNestedRow';
import PageNavigationContext from './PageNavigationContext';
import type { OnSelectType, PageNavigationItem } from './types';

import { Text } from '../../display/Text';
import { Link } from '../../navigation/Link';

import styles from './PageNavigation.module.scss';

type Props = {
  /** Which item, if any, should be first selected. */
  initiallySelected?: string;
  /** An array of (possibly) nested items to display. */
  items: Array<PageNavigationItem>;
  /** Does this component support nested items, i.e. children properties? */
  nested?: boolean;
  /** An optional callback for clicks or pressing return. */
  onSelect?: OnSelectType;
  /** Option to show a "expand all" button above the title. */
  showExpandAllButton?: boolean;
  /** An optional header to place at the top of the navigation. */
  title?: string;
};

const getInitialExpansionStateLookupTable = (
  items: Array<PageNavigationItem>
): Record<string, boolean> => {
  const lookupTable: Record<string, boolean> = {};

  for (const item of items) {
    lookupTable[item.id] = item.initiallyExpanded || false;
  }

  return lookupTable;
};

/**
 * <span style="color: #b83519; font-weight: bold;">⚠️ Consider using [SecondaryNav β](../?path=/docs/beta-secondarynav--docs) if it meets your needs instead.</span>
 *
 * A collapsible page navigation system up to 3 levels deep.
 */
export function PageNavigation({
  initiallySelected,
  items,
  nested,
  onSelect,
  showExpandAllButton = false,
  title,
}: Props) {
  const [id, setId] = useState(initiallySelected || '');
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  // Ultimately a object tracking state via {rowId: isExpanded} mappings.
  const [expansionStateLookupTable, setExpansionStateLookupTable] = useState(
    getInitialExpansionStateLookupTable(items)
  );

  const isExpanded = (rowId: string): boolean => {
    return expansionStateLookupTable[rowId] || false;
  };

  const toggleExpand = (rowId: string) => {
    setExpansionStateLookupTable((lookupTable) => {
      return {
        ...lookupTable,
        [rowId]: !lookupTable[rowId],
      };
    });
  };

  const expandAll = () => {
    setExpansionStateLookupTable((lookupTable) => {
      const newLookupTable = { ...lookupTable };
      for (const item in newLookupTable) {
        newLookupTable[item] = !isAllExpanded;
      }

      setIsAllExpanded((allExpanded) => !allExpanded);
      return newLookupTable;
    });
  };

  if (!items) {
    return null;
  }

  const showCount = items.some((item) => (item.count || 0) > 0);

  return (
    <PageNavigationContext.Provider value={{ id, setId, toggleExpand }}>
      {nested && showExpandAllButton && (
        <div
          onClick={expandAll}
          onKeyPress={expandAll}
          data-testid="pagenavigation-expandall-button"
          role="button"
          tabIndex={0}
          className={styles.expandAllButton}
          data-zds
        >
          <Link>{isAllExpanded ? 'Collapse all' : 'Expand all'}</Link>
        </div>
      )}

      {title && (
        <div className={styles.title} data-zds>
          <Text tag="p" type="ParagraphHeader3">
            {title}
          </Text>
        </div>
      )}

      <ul className={styles.list} data-zds>
        {nested
          ? items.map((item) => (
              <NestedFirstRow
                item={item}
                key={item.id}
                isExpanded={isExpanded(item.id)}
                onSelect={onSelect}
              />
            ))
          : items.map((item) => (
              <NonNestedRow
                item={item}
                key={item.id}
                onSelect={onSelect}
                showCount={showCount}
              />
            ))}
      </ul>
    </PageNavigationContext.Provider>
  );
}
